import { api } from "../api/client.ts";
import type { ObjEntity, SpaceOut } from "../api/types.gen.ts";
import { t } from "../i18n/index.ts";
import type { Action } from "../lib/actions.ts";
import { commentCount } from "../lib/commentCounts.ts";
import { useEntities } from "../lib/data.ts";
import { spaceApi, spaceUrl } from "../lib/paths.ts";
import { bump, useAction } from "../lib/resource.ts";
import { canEditSchema } from "../lib/roles.ts";
import { MAX_ENTITIES } from "../lib/schemaLimits.ts";
import type { PageProps } from "../lib/state.ts";
import { useId } from "../lib/uid.ts";
import { Button, IconButton, Link } from "../ui/Button.tsx";
import { CommentCount } from "../ui/counts.tsx";
import { EntityGlyph } from "../ui/EntityGlyph.tsx";
import { CatalogIcon, PinIcon, PinnedIcon, PlusIcon } from "../ui/icons.tsx";
import { Async, EmptyState, Notice } from "../ui/misc.tsx";
import { buttonClass, ICON_ONLY_NARROW, LINK } from "../ui/styles.ts";
import { Table, Td, Th } from "../ui/Table.tsx";
import { ItemsNav } from "./items/nav.tsx";

/** Pins an entity to the sidebar, or unpins it, in one click. */
function PinToggle({ space, entity }: { space: SpaceOut; entity: ObjEntity }) {
  const [toggle, busy] = useAction(async () => {
    await api.patch(spaceApi(space, `/entities/${entity.id}`), { pinned: !entity.pinned, ver: entity.ver });
    bump(); // the list and the sidebar
  });
  const pinned = entity.pinned ?? false;
  const label = t(pinned ? "entities.unpin" : "entities.pin", { name: entity.name });
  return (
    <IconButton aria-pressed={pinned} label={label} disabled={busy || !canEditSchema(space)} onClick={() => toggle()}>
      {pinned ? <PinnedIcon /> : <PinIcon />}
    </IconButton>
  );
}

/** "New entity", or, once the space has as many as it may, the same button disabled and why. */
function NewEntity({ space, count }: { space: SpaceOut; count: number }) {
  const id = useId();
  if (count < MAX_ENTITIES) {
    return (
      <Link href={spaceUrl(space, "/entities/new")} class={`${buttonClass("primary")} ${ICON_ONLY_NARROW}`}>
        <PlusIcon />
        {t("entities.new")}
      </Link>
    );
  }
  const why = t("entities.limitReached", { limit: MAX_ENTITIES });
  return (
    <span class="flex items-center gap-3">
      <span id={id} class="text-xs text-zinc-500">
        {why}
      </span>
      <Button variant="primary" disabled={true} aria-describedby={id} class={ICON_ONLY_NARROW}>
        <PlusIcon />
        {t("entities.new")}
      </Button>
    </span>
  );
}

/** The list's header's actions: adding from the catalog, and "New entity" (disabled, and why, at the limit). */
function headerActions(space: SpaceOut, count: number): Action[] {
  return [
    {
      key: "catalog",
      label: t("entities.catalog"),
      // Our own picture: on a narrow screen only (docs/ICONS.md).
      icon: <CatalogIcon class="sm:hidden" />,
      symbol: "books.vertical",
      href: spaceUrl(space, "/entities/catalog"),
    },
    {
      key: "new",
      label: t("entities.new"),
      icon: <PlusIcon />,
      symbol: "plus",
      primary: true,
      fab: true,
      href: spaceUrl(space, "/entities/new"),
      ...(count < MAX_ENTITIES ? {} : { disabled: true, node: <NewEntity space={space} count={count} /> }),
    },
  ];
}

export function Entities({ space }: PageProps) {
  const list = useEntities(space);
  const count = list.data?.length ?? 0;
  return (
    <>
      <ItemsNav space={space} tab="entities" buttons={canEditSchema(space) ? headerActions(space, count) : []} />
      <Notice when={space.schema_locked}>{t("entities.locked")}</Notice>
      <Async r={list}>
        {(entities) =>
          entities.length === 0 ? (
            <EmptyState title={t("entities.empty")}>
              {t("entities.emptyHint")}
              {canEditSchema(space) ? (
                <>
                  {" "}
                  <Link href={spaceUrl(space, "/entities/catalog")} class={LINK}>
                    {t("entities.emptyCatalog")}
                  </Link>
                </>
              ) : null}
            </EmptyState>
          ) : (
            <Table stack={true}>
              <thead class="bg-zinc-50">
                <tr>
                  <Th>
                    <span class="sr-only">{t("entities.pinned")}</span>
                  </Th>
                  <Th>{t("entities.name")}</Th>
                  <Th>{t("entities.fields")}</Th>
                  <Th>{t("entities.description")}</Th>
                  <Th />
                </tr>
              </thead>
              <tbody class="divide-y divide-zinc-100">
                {entities.map((e) => (
                  <tr key={e.id}>
                    <Td class="w-px pr-0" label="">
                      <PinToggle space={space} entity={e} />
                    </Td>
                    <Td>
                      <span class="flex items-center gap-2">
                        <EntityGlyph icon={e.icon} size={20} empty="dot" />
                        <Link href={spaceUrl(space, `/entities/${e.id}`)} class={LINK}>
                          {e.name}
                        </Link>
                        <CommentCount
                          count={commentCount(space.id, "entities", e.id)}
                          href={spaceUrl(space, `/entities/${e.id}?tab=conversation`)}
                        />
                      </span>
                    </Td>
                    <Td label={t("entities.fields")} wide={true}>
                      {(e.fields ?? []).map((f) => f.label || f.key).join(", ")}
                    </Td>
                    <Td label={t("entities.description")} wide={true}>
                      {e.description}
                    </Td>
                    <Td align="right" label="">
                      <Link href={`${spaceUrl(space, "/items")}?entity=${e.id}`} class={buttonClass("secondary", "sm")}>
                        {t("entities.viewItems")}
                      </Link>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )
        }
      </Async>
    </>
  );
}
